import { ProjectStepResponse, ProjectSectionResponse, ProjectResponse } from '@business/entities/project';
import { TimeLogResponse } from '@business/entities/time_log';
import { CommonService } from '../common_service';
import { R } from '@core/utils/r';

export interface UserTimeLog {
  userId: string;
  userName: string;
  userEmail: string;
  timeTracked: number; // seconds
  logCount: number;
}

/**
 * Get total tracked time (in seconds) for a specific step from time logs
 */
export function getStepTrackedTime(stepId: string, timeLogs: TimeLogResponse[]): number {
  return timeLogs.filter(log => log.objectId === stepId && log.type === 'projectTask').reduce((sum, log) => sum + (log.time || 0), 0);
}

/**
 * Get total tracked time (in seconds) for a step including all sub-tasks recursively
 */
export function getStepTotalTrackedTime(step: ProjectStepResponse, timeLogs: TimeLogResponse[]): number {
  if (!step?.id) {
    return 0;
  }
  let total = getStepTrackedTime(step.id, timeLogs);

  if (step.subSteps && step.subSteps.length > 0) {
    for (const subStep of step.subSteps) {
      total += getStepTotalTrackedTime(subStep, timeLogs);
    }
  }

  return total;
}

/**
 * Get total estimate (in minutes) for a step including all sub-tasks recursively
 */
export function getStepTotalEstimate(step: ProjectStepResponse): number {
  let total = step.timeEstimate || 0;

  if (step.subSteps && step.subSteps.length > 0) {
    for (const subStep of step.subSteps) {
      total += getStepTotalEstimate(subStep);
    }
  }

  return total;
}

/**
 * Get total tracked time (in seconds) for a section
 */
export function getSectionTrackedTime(section: ProjectSectionResponse, timeLogs: TimeLogResponse[]): number {
  return section.steps.reduce((sum, step) => sum + getStepTotalTrackedTime(step, timeLogs), 0);
}

/**
 * Get total estimate (in minutes) for a section
 */
export function getSectionTotalEstimate(section: ProjectSectionResponse): number {
  return section.steps.reduce((sum, step) => sum + getStepTotalEstimate(step), 0);
}

/**
 * Get total tracked time (in seconds) for entire project
 */
export function getProjectTrackedTime(project: ProjectResponse, timeLogs: TimeLogResponse[]): number {
  return project.sections.reduce((sum, section) => sum + getSectionTrackedTime(section, timeLogs), 0);
}

/**
 * Get total estimate (in minutes) for entire project
 */
export function getProjectTotalEstimate(project: ProjectResponse): number {
  return project.sections.reduce((sum, section) => sum + getSectionTotalEstimate(section), 0);
}

/**
 * Get time logs grouped by user for a specific step
 */
export function getStepTimeLogsByUser(stepId: string, timeLogs: TimeLogResponse[]): UserTimeLog[] {
  const stepLogs = timeLogs.filter(log => log.objectId === stepId && log.type === 'projectTask');

  const grouped = R.groupBy(stepLogs, log => log.user.id);

  return Object.entries(grouped)
    .map(([userId, logs]) => {
      const firstLog = logs[0];
      return {
        userId,
        userName: CommonService.getFullName(firstLog.user),
        userEmail: firstLog.user.email,
        timeTracked: logs.reduce((sum, log) => sum + (log.time || 0), 0),
        logCount: logs.length,
      };
    })
    .sort((a, b) => b.timeTracked - a.timeTracked); // Sort by time tracked (highest first)
}

export function isStepBillable(stepId: string, timeLogs: TimeLogResponse[]): boolean {
  return timeLogs.filter(log => R.toString(log.objectId) === R.toString(stepId)).some(log => log.isBillable);
}

export function getStepBillableTime(stepId: string, timeLogs: TimeLogResponse[]): number {
  return timeLogs.filter(log => R.toString(log.objectId) === R.toString(stepId) && log.isBillable).reduce((acc, log) => acc + (log.time || 0), 0);
}

export function getStepNonBillableTime(stepId: string, timeLogs: TimeLogResponse[]): number {
  return timeLogs.filter(log => R.toString(log.objectId) === R.toString(stepId) && !log.isBillable).reduce((acc, log) => acc + (log.time || 0), 0);
}

export function getStepTotalBillableTime(step: ProjectStepResponse, timeLogs: TimeLogResponse[]): number {
  let total = getStepBillableTime(step.id, timeLogs);

  if (step.subSteps && step.subSteps.length > 0) {
    for (const subStep of step.subSteps) {
      total += getStepTotalBillableTime(subStep, timeLogs);
    }
  }

  return total;
}

export function getStepTotalNonBillableTime(step: ProjectStepResponse, timeLogs: TimeLogResponse[]): number {
  let total = getStepNonBillableTime(step.id, timeLogs);

  if (step.subSteps && step.subSteps.length > 0) {
    for (const subStep of step.subSteps) {
      total += getStepTotalNonBillableTime(subStep, timeLogs);
    }
  }

  return total;
}

export function getStepBillableBreakdown(
  step: ProjectStepResponse,
  timeLogs: TimeLogResponse[],
): {
  billable: number;
  nonBillable: number;
  total: number;
  billablePercentage: number;
} {
  const billable = getStepTotalBillableTime(step, timeLogs);
  const nonBillable = getStepTotalNonBillableTime(step, timeLogs);
  const total = billable + nonBillable;
  const billablePercentage = total > 0 ? (billable / total) * 100 : 0;

  return {
    billable,
    nonBillable,
    total,
    billablePercentage,
  };
}

export function getSectionBillableTime(section: ProjectSectionResponse, timeLogs: TimeLogResponse[]): number {
  return section.steps.reduce((sum, step) => sum + getStepTotalBillableTime(step, timeLogs), 0);
}

export function getSectionNonBillableTime(section: ProjectSectionResponse, timeLogs: TimeLogResponse[]): number {
  return section.steps.reduce((sum, step) => sum + getStepTotalNonBillableTime(step, timeLogs), 0);
}

export function getProjectBillableBreakdown(
  project: ProjectResponse,
  timeLogs: TimeLogResponse[],
): {
  billable: number;
  nonBillable: number;
  total: number;
  billablePercentage: number;
} {
  const billable = project.sections.reduce((sum, section) => sum + getSectionBillableTime(section, timeLogs), 0);
  const nonBillable = project.sections.reduce((sum, section) => sum + getSectionNonBillableTime(section, timeLogs), 0);
  const total = billable + nonBillable;
  const billablePercentage = total > 0 ? (billable / total) * 100 : 0;

  return {
    billable,
    nonBillable,
    total,
    billablePercentage,
  };
}

export function getStepTimeLogsWithBillable(stepId: string, timeLogs: TimeLogResponse[]): TimeLogResponse[] {
  return timeLogs.filter(log => R.toString(log.objectId) === R.toString(stepId) && log.type === 'projectTask');
}

export function getUserStepTrackedTime(stepId: string, userId: string, timeLogs: TimeLogResponse[]): number {
  return timeLogs
    .filter(log => R.toString(log.objectId) === R.toString(stepId) && R.toString(log.user?.id) === R.toString(userId))
    .reduce((sum, log) => sum + (log.time || 0), 0);
}

export function getUserStepTotalTrackedTime(step: ProjectStepResponse, userId: string, timeLogs: TimeLogResponse[]): number {
  let total = getUserStepTrackedTime(step.id, userId, timeLogs);

  if (step.subSteps && step.subSteps.length > 0) {
    for (const subStep of step.subSteps) {
      total += getUserStepTotalTrackedTime(subStep, userId, timeLogs);
    }
  }

  return total;
}
